import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { PssAuthService } from '../../services/pss-auth.service';
import { FeatureFlagService, FeatureFlag, UserGrant, FleetGrant, GrantGroup, GrantGroupEntry } from '../../services/feature-flag.service';

interface AdminAccount {
  id: number;
  userId: string;
  userName: string;
  label: string;
  email: string;
  pssServer: string;
  shipLevel: string;
  shipName: string;
  fleetName: string;
  trophies: string;
  isServiceAccount: boolean;
  isAdmin: boolean;
  isActive: boolean;
  isLocked: boolean;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PollingTask {
  id: number;
  taskKey: string;
  description: string;
  taskType: string;
  targetId: string | null;
  ownerAccountId: number | null;
  scheduleType: string;
  intervalSec: number;
  cronExpression: string | null;
  isActive: boolean;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
  assignedAccounts: { id: number; userId: string; userName: string; label: string; isActive: boolean }[];
}

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.css']
})
export class AdminComponent implements OnInit {
  activeTab: 'accounts' | 'tasks' | 'feature-flags' = 'accounts';
  flagsSubTab: 'flags' | 'groups' = 'flags';

  accounts: AdminAccount[] = [];
  accountsLoading = false;
  accountsError = '';
  searchAccountText = '';

  get filteredAccounts(): AdminAccount[] {
    const term = this.searchAccountText.trim().toLowerCase();
    if (!term) return this.accounts;
    return this.accounts.filter(a =>
      (a.userName || '').toLowerCase().includes(term) ||
      (a.userId || '').toLowerCase().includes(term) ||
      (a.email || '').toLowerCase().includes(term) ||
      (a.fleetName || '').toLowerCase().includes(term) ||
      (a.label || '').toLowerCase().includes(term));
  }

  tasks: PollingTask[] = [];
  tasksLoading = false;
  tasksError = '';

  // New account form
  newAccount = { userId: '', userName: '', label: '', email: '', deviceKey: '',
                 isServiceAccount: false, isAdmin: false };
  newAccountError = '';
  newAccountSaving = false;

  // New task form
  newTask = { taskKey: '', description: '', taskType: 'top100_fleets', targetId: '',
              scheduleType: 'interval', intervalSec: 10, cronExpression: '' };
  newTaskError = '';
  newTaskSaving = false;

  // Assign account to task
  assignAccountId: { [taskId: number]: number | null } = {};
  assignError: { [taskId: number]: string } = {};

  constructor(private http: HttpClient, private auth: PssAuthService, private ffSvc: FeatureFlagService, private router: Router) {}

  ngOnInit(): void {
    this.verifyAdminAccess();
    this.loadAccounts();
    this.loadTasks();
    this.loadFeatureFlags();
    this.loadGrantGroups();
  }

  exitPanel(): void {
    this.router.navigate(['/dashboard']);
  }

  refreshAccounts(): void {
    this.loadAccounts();
  }

  private verifyAdminAccess(): void {
    if (!this.auth.currentSession?.isAdmin) {
      // If local session is not admin, redirect and prevent loading admin content
      this.router.navigate(['/dashboard']);
      return;
    }
  }

  private get headers(): HttpHeaders {
    const session = this.auth.currentSession;
    return new HttpHeaders({ 'X-User-Id': session?.userId ?? '' });
  }

  // ── Accounts ──────────────────────────────────────────────────────────────

  loadAccounts(): void {
    this.accountsLoading = true;
    this.accountsError = '';
    this.http.get<{ accounts: AdminAccount[] }>('/api/admin/accounts', { headers: this.headers })
      .subscribe({
        next: r => { this.accounts = r.accounts; this.accountsLoading = false; },
        error: e => {
          if (e?.status === 403) {
            this.router.navigate(['/dashboard']);
            return;
          }
          this.accountsError = e?.error?.error ?? 'Failed to load accounts';
          this.accountsLoading = false;
        }
      });
  }

  saveNewAccount(): void {
    if (!this.newAccount.userId) { this.newAccountError = 'PSS User ID is required'; return; }
    this.newAccountSaving = true;
    this.newAccountError = '';
    this.http.post<{ account: AdminAccount }>('/api/admin/accounts', this.newAccount, { headers: this.headers })
      .subscribe({
        next: r => {
          const idx = this.accounts.findIndex(a => a.id === r.account.id);
          if (idx >= 0) this.accounts[idx] = r.account; else this.accounts.push(r.account);
          this.newAccount = { userId: '', userName: '', label: '', email: '', deviceKey: '',
                              isServiceAccount: false, isAdmin: false };
          this.newAccountSaving = false;
        },
        error: e => { this.newAccountError = e?.error?.error ?? 'Save failed'; this.newAccountSaving = false; }
      });
  }

  toggleFlag(account: AdminAccount, flag: 'isServiceAccount' | 'isAdmin' | 'isActive' | 'isLocked'): void {
    const patch: Partial<AdminAccount> = { [flag]: !account[flag] };
    this.http.patch<{ account: AdminAccount }>(`/api/admin/accounts/${account.id}`, patch, { headers: this.headers })
      .subscribe({
        next: r => {
          const idx = this.accounts.findIndex(a => a.id === account.id);
          if (idx >= 0) this.accounts[idx] = r.account;
        },
        error: () => {
          this.accountsError = 'Failed to update account flag; please refresh.';
        }
      });
  }

  deleteAccount(account: AdminAccount): void {
    if (!confirm(`Delete account "${account.userName || account.userId}"?`)) return;
    this.http.delete(`/api/admin/accounts/${account.id}`, { headers: this.headers })
      .subscribe({
        next: () => { this.accounts = this.accounts.filter(a => a.id !== account.id); },
        error: () => {}
      });
  }

  // ── Polling tasks ─────────────────────────────────────────────────────────

  loadTasks(): void {
    this.tasksLoading = true;
    this.tasksError = '';
    this.http.get<{ tasks: PollingTask[] }>('/api/admin/polling-tasks', { headers: this.headers })
      .subscribe({
        next: r => { this.tasks = r.tasks; this.tasksLoading = false; },
        error: e => { this.tasksError = e?.error?.error ?? 'Failed to load tasks'; this.tasksLoading = false; }
      });
  }

  saveNewTask(): void {
    if (!this.newTask.taskKey || !this.newTask.taskType) {
      this.newTaskError = 'Task key and type are required'; return;
    }
    this.newTaskSaving = true;
    this.newTaskError = '';
    const body = { ...this.newTask, targetId: this.newTask.targetId || null,
                   cronExpression: this.newTask.cronExpression || null };
    this.http.post<{ task: PollingTask }>('/api/admin/polling-tasks', body, { headers: this.headers })
      .subscribe({
        next: r => {
          this.tasks.push(r.task);
          this.newTask = { taskKey: '', description: '', taskType: 'top100_fleets', targetId: '',
                          scheduleType: 'interval', intervalSec: 10, cronExpression: '' };
          this.newTaskSaving = false;
        },
        error: e => { this.newTaskError = e?.error?.error ?? 'Save failed'; this.newTaskSaving = false; }
      });
  }

  toggleTaskActive(task: PollingTask): void {
    this.http.patch<{ task: PollingTask }>(`/api/admin/polling-tasks/${task.id}`,
      { isActive: !task.isActive }, { headers: this.headers })
      .subscribe({
        next: r => {
          const idx = this.tasks.findIndex(t => t.id === task.id);
          if (idx >= 0) this.tasks[idx] = r.task;
        },
        error: () => {}
      });
  }

  deleteTask(task: PollingTask): void {
    if (!confirm(`Delete task "${task.taskKey}"?`)) return;
    this.http.delete(`/api/admin/polling-tasks/${task.id}`, { headers: this.headers })
      .subscribe({
        next: () => { this.tasks = this.tasks.filter(t => t.id !== task.id); },
        error: () => {}
      });
  }

  assignAccount(task: PollingTask): void {
    const accountId = this.assignAccountId[task.id];
    if (!accountId) return;
    this.assignError[task.id] = '';
    this.http.post<{ taskId: number; accountId: number }>(
      `/api/admin/polling-tasks/${task.id}/accounts`, { accountId }, { headers: this.headers })
      .subscribe({
        next: () => {
          const acct = this.accounts.find(a => a.id === accountId)!;
          task.assignedAccounts.push({ id: acct.id, userId: acct.userId, userName: acct.userName,
                                       label: acct.label, isActive: acct.isActive });
          this.assignAccountId[task.id] = null;
        },
        error: e => { this.assignError[task.id] = e?.error?.error ?? 'Assign failed'; }
      });
  }

  unassignAccount(task: PollingTask, accountId: number): void {
    this.http.delete(`/api/admin/polling-tasks/${task.id}/accounts/${accountId}`, { headers: this.headers })
      .subscribe({
        next: () => { task.assignedAccounts = task.assignedAccounts.filter(a => a.id !== accountId); },
        error: () => {}
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  get serviceAccounts(): AdminAccount[] {
    return this.accounts.filter(a => a.isServiceAccount && a.isActive);
  }

  scheduleLabel(task: PollingTask): string {
    if (task.scheduleType === 'cron') return task.cronExpression ?? 'cron';
    return `every ${task.intervalSec}s`;
  }

  isAlreadyAssigned(task: PollingTask, accountId: number): boolean {
    return task.assignedAccounts.some(a => a.id === accountId);
  }

  // ── Feature Flags ──────────────────────────────────────────────────────────

  flags: FeatureFlag[] = [];
  flagsLoading = false;
  flagsError = '';

  // expanded grant view
  expandedFlagKey: string | null = null;
  expandedUserGrants: UserGrant[] = [];
  expandedFleetGrants: FleetGrant[] = [];
  grantsLoading = false;
  expandedGrantMode: 'user' | 'fleet' = 'user';

  // new-flag form
  newFlag = { flagKey: '', displayName: '', description: '', requiredTierRank: 0, isEnabled: true };
  newFlagError = '';
  newFlagSaving = false;

  // add-grant forms
  newUserGrantUserId = '';
  newUserGrantUserName = '';
  newUserGrantNote = '';
  newUserGrantError = '';

  userSearchTerm = '';
  userSearchResults: Array<{ userId: string; userName: string; fleetName: string }> = [];
  userSearchLoading = false;
  userSearchError = '';

  newFleetGrantAllianceId = '';
  newFleetGrantFleetName = '';
  newFleetGrantNote = '';
  newFleetGrantError = '';

  fleetSearchTerm = '';
  fleetSearchResults: Array<{ userId: string; userName: string; fleetName: string }> = [];
  fleetSearchLoading = false;
  fleetSearchError = '';
  loadFeatureFlags(): void {
    this.flagsLoading = true;
    this.flagsError = '';
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.listFlags(userId).subscribe({
      next: r => { this.flags = r.flags; this.flagsLoading = false; },
      error: e => { this.flagsError = e?.error?.error ?? 'Failed to load flags'; this.flagsLoading = false; }
    });
  }

  saveNewFlag(): void {
    if (!this.newFlag.flagKey || !this.newFlag.displayName) {
      this.newFlagError = 'Flag key and display name are required'; return;
    }
    this.newFlagSaving = true;
    this.newFlagError = '';
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.createFlag(userId, this.newFlag).subscribe({
      next: r => {
        this.flags = [...this.flags.filter(f => f.flagKey !== r.flag.flagKey), r.flag]
          .sort((a, b) => a.flagKey.localeCompare(b.flagKey));
        this.newFlag = { flagKey: '', displayName: '', description: '', requiredTierRank: 0, isEnabled: true };
        this.newFlagSaving = false;
      },
      error: e => { this.newFlagError = e?.error?.error ?? 'Save failed'; this.newFlagSaving = false; }
    });
  }

  toggleFlagEnabled(flag: FeatureFlag): void {
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.updateFlag(userId, flag.flagKey, { isEnabled: !flag.isEnabled }).subscribe({
      next: r => {
        const idx = this.flags.findIndex(f => f.flagKey === r.flag.flagKey);
        if (idx >= 0) this.flags[idx] = r.flag;
      },
      error: () => {}
    });
  }

  setGrantMode(mode: 'user' | 'fleet'): void {
    this.expandedGrantMode = mode;
  }

  deleteFlag(flag: FeatureFlag): void {
    if (!confirm(`Delete flag "${flag.flagKey}"? This removes all grants too.`)) return;
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.deleteFlag(userId, flag.flagKey).subscribe({
      next: () => { this.flags = this.flags.filter(f => f.flagKey !== flag.flagKey); if (this.expandedFlagKey === flag.flagKey) this.expandedFlagKey = null; },
      error: () => {}
    });
  }

  expandFlag(flag: FeatureFlag): void {
    if (this.expandedFlagKey === flag.flagKey) { this.expandedFlagKey = null; return; }
    this.expandedFlagKey = flag.flagKey;
    this.expandedGrantMode = 'user';
    this.expandedUserGrants = [];
    this.expandedFleetGrants = [];
    this.grantsLoading = true;
    const userId = this.auth.currentSession?.userId ?? '';
    const ug$ = this.ffSvc.listUserGrants(userId, flag.flagKey);
    const fg$ = this.ffSvc.listFleetGrants(userId, flag.flagKey);
    ug$.subscribe({ next: r => { this.expandedUserGrants = r.grants; this.grantsLoading = false; }, error: () => { this.grantsLoading = false; } });
    fg$.subscribe({ next: r => this.expandedFleetGrants = r.grants, error: () => {} });
  }

  searchUsers(): void {
    this.userSearchError = '';
    this.userSearchResults = [];
    const query = (this.userSearchTerm || '').trim();
    if (!query) {
      this.userSearchError = 'Enter name or user ID to search';
      return;
    }
    this.userSearchLoading = true;
    const adminId = this.auth.currentSession?.userId ?? '';
    const token   = this.auth.currentSession?.accessToken ?? '';
    const server  = this.auth.currentSession?.productionServer ?? 'api2.pixelstarships.com';
    this.ffSvc.searchUsers(adminId, query).subscribe({
      next: r => {
        this.userSearchResults = (r.accounts || []).map(a => ({ userId: a.userId, userName: a.userName, fleetName: a.fleetName }));
        if (!this.userSearchResults.length) {
          // fallback to PSS API if local DB didn't find anything
          this.ffSvc.searchPssUsers(adminId, query, token, server).subscribe({
            next: rr => {
              this.userSearchResults = (rr.accounts || []).map(a => ({ userId: a.userId, userName: a.userName, fleetName: a.fleetName || '' }));
              if (!this.userSearchResults.length) this.userSearchError = 'No users found';
              this.userSearchLoading = false;
            },
            error: ee => {
              this.userSearchError = ee?.error?.error ?? 'Search failed';
              this.userSearchLoading = false;
            }
          });
          return;
        }
        this.userSearchLoading = false;
      },
      error: e => {
        // local search failed, still try PSS API
        this.ffSvc.searchPssUsers(adminId, query, token, server).subscribe({
          next: rr => {
            this.userSearchResults = (rr.accounts || []).map(a => ({ userId: a.userId, userName: a.userName, fleetName: a.fleetName || '' }));
            if (!this.userSearchResults.length) this.userSearchError = 'No users found';
            this.userSearchLoading = false;
          },
          error: ee => {
            this.userSearchError = ee?.error?.error ?? 'Search failed';
            this.userSearchLoading = false;
          }
        });
      }
    });
  }

  chooseUserFromSearch(user: { userId: string; userName: string; fleetName: string }): void {
    this.newUserGrantUserId = user.userId;
    this.newUserGrantUserName = user.userName || '';
    this.userSearchTerm = '';
    this.userSearchResults = [];
    this.userSearchError = '';
  }

  searchFleets(): void {
    this.fleetSearchError = '';
    this.fleetSearchResults = [];
    const query = (this.fleetSearchTerm || '').trim();
    if (!query) {
      this.fleetSearchError = 'Enter fleet name or numeric ID to search';
      return;
    }
    this.fleetSearchLoading = true;
    const adminId    = this.auth.currentSession?.userId ?? '';
    const token      = this.auth.currentSession?.accessToken ?? '';
    const server     = this.auth.currentSession?.productionServer ?? 'api2.pixelstarships.com';
    // Always use PSS API via the /pss/ proxy with the admin's own session token.
    this.ffSvc.searchPssFleets(adminId, query, token, server).subscribe({
      next: rr => {
        const results = (rr.accounts || []).map((a: any) => ({
          userId:    a.allianceId || '',
          userName:  a.allianceName || '',
          fleetName: a.allianceName || '',
        }));
        const seen = new Set<string>();
        this.fleetSearchResults = results.filter(item => {
          if (!item.userId) return false;
          if (seen.has(item.userId)) return false;
          seen.add(item.userId);
          return true;
        });
        if (!this.fleetSearchResults.length) this.fleetSearchError = 'No fleets found';
        this.fleetSearchLoading = false;
      },
      error: ee => {
        this.fleetSearchError = ee?.error?.error ?? 'Search failed';
        this.fleetSearchLoading = false;
      }
    });
  }

  chooseFleetFromSearch(fleet: { userId: string; userName: string; fleetName: string }): void {
    this.newFleetGrantAllianceId = fleet.userId || '';
    this.newFleetGrantFleetName = fleet.fleetName || fleet.userName || '';
    this.fleetSearchTerm = '';
    this.fleetSearchResults = [];
    this.fleetSearchError = '';
  }

  addUserGrant(): void {
    if (!this.expandedFlagKey || !this.newUserGrantUserId) { this.newUserGrantError = 'User ID required'; return; }
    this.newUserGrantError = '';
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.addUserGrant(userId, this.expandedFlagKey, this.newUserGrantUserId, this.newUserGrantNote || undefined).subscribe({
      next: () => {
        this.expandedUserGrants = [...this.expandedUserGrants, { id: 0, flagKey: this.expandedFlagKey!, userId: this.newUserGrantUserId, userName: this.newUserGrantUserName || undefined, note: this.newUserGrantNote || undefined, createdAt: new Date().toISOString() }];
        this.newUserGrantUserId = '';
        this.newUserGrantUserName = '';
        this.newUserGrantNote = '';
        const f = this.flags.find(x => x.flagKey === this.expandedFlagKey);
        if (f) f.userGrantCount = (f.userGrantCount ?? 0) + 1;
        this.loadFeatureFlags();
      },
      error: e => { this.newUserGrantError = e?.error?.error ?? 'Failed'; }
    });
  }

  removeUserGrant(grant: UserGrant): void {
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.removeUserGrant(userId, grant.flagKey, grant.userId).subscribe({
      next: () => {
        this.expandedUserGrants = this.expandedUserGrants.filter(g => g.userId !== grant.userId);
        const f = this.flags.find(x => x.flagKey === grant.flagKey);
        if (f && f.userGrantCount) f.userGrantCount--;
      },
      error: () => {}
    });
  }

  addFleetGrant(): void {
    if (!this.expandedFlagKey || !this.newFleetGrantAllianceId) { this.newFleetGrantError = 'Alliance ID required'; return; }
    this.newFleetGrantError = '';
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.addFleetGrant(userId, this.expandedFlagKey, this.newFleetGrantAllianceId, this.newFleetGrantFleetName || undefined, this.newFleetGrantNote || undefined).subscribe({
      next: () => {
        this.expandedFleetGrants = [...this.expandedFleetGrants, { id: 0, flagKey: this.expandedFlagKey!, allianceId: this.newFleetGrantAllianceId, fleetName: this.newFleetGrantFleetName || undefined, note: this.newFleetGrantNote || undefined, createdAt: new Date().toISOString() }];
        this.newFleetGrantAllianceId = '';
        this.newFleetGrantFleetName = '';
        this.newFleetGrantNote = '';
        const f = this.flags.find(x => x.flagKey === this.expandedFlagKey);
        if (f) f.fleetGrantCount = (f.fleetGrantCount ?? 0) + 1;
        this.loadFeatureFlags();
      },
      error: e => { this.newFleetGrantError = e?.error?.error ?? 'Failed'; }
    });
  }

  removeFleetGrant(grant: FleetGrant): void {
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.removeFleetGrant(userId, grant.flagKey, grant.allianceId).subscribe({
      next: () => {
        this.expandedFleetGrants = this.expandedFleetGrants.filter(g => g.allianceId !== grant.allianceId);
        const f = this.flags.find(x => x.flagKey === grant.flagKey);
        if (f && f.fleetGrantCount) f.fleetGrantCount--;
      },
      error: () => {}
    });
  }

  // ── Grant Groups ──────────────────────────────────────────────────────────

  groups: GrantGroup[] = [];
  groupsLoading = false;
  groupsError = '';

  newGroup = { name: '', note: '' };
  newGroupError = '';
  newGroupSaving = false;

  expandedGroupId: number | null = null;
  expandedGroupEntries: GrantGroupEntry[] = [];
  groupEntriesLoading = false;

  // Add entry to a group
  newGroupEntryType: 'user' | 'fleet' = 'user';
  newGroupEntryId   = '';
  newGroupEntryName = '';
  newGroupEntryError = '';

  // User search scoped to group entry form
  groupUserSearchTerm    = '';
  groupUserSearchResults: Array<{ userId: string; userName: string; fleetName: string }> = [];
  groupUserSearchLoading = false;
  groupUserSearchError   = '';

  // Fleet search scoped to group entry form
  groupFleetSearchTerm    = '';
  groupFleetSearchResults: Array<{ userId: string; userName: string; fleetName: string }> = [];
  groupFleetSearchLoading = false;
  groupFleetSearchError   = '';

  // Apply group to flags
  applyGroupId: number | null = null;
  applyGroupFlagKeys: Set<string> = new Set();
  applyGroupLoading = false;
  applyGroupError   = '';
  applyGroupSuccess = '';

  get userGroupEntries(): GrantGroupEntry[] {
    return this.expandedGroupEntries.filter(e => e.entryType === 'user');
  }

  get fleetGroupEntries(): GrantGroupEntry[] {
    return this.expandedGroupEntries.filter(e => e.entryType === 'fleet');
  }

  loadGrantGroups(): void {
    this.groupsLoading = true;
    this.groupsError = '';
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.listGrantGroups(userId).subscribe({
      next: r => { this.groups = r.groups; this.groupsLoading = false; },
      error: e => { this.groupsError = e?.error?.error ?? 'Failed to load groups'; this.groupsLoading = false; }
    });
  }

  saveNewGroup(): void {
    if (!this.newGroup.name.trim()) { this.newGroupError = 'Name is required'; return; }
    this.newGroupSaving = true;
    this.newGroupError = '';
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.createGrantGroup(userId, this.newGroup.name.trim(), this.newGroup.note || undefined).subscribe({
      next: r => {
        this.groups = [...this.groups, r.group].sort((a, b) => a.name.localeCompare(b.name));
        this.newGroup = { name: '', note: '' };
        this.newGroupSaving = false;
      },
      error: e => { this.newGroupError = e?.error?.error ?? 'Save failed'; this.newGroupSaving = false; }
    });
  }

  deleteGroup(group: GrantGroup): void {
    if (!confirm(`Delete group "${group.name}"? This does not remove existing flag grants.`)) return;
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.deleteGrantGroup(userId, group.id).subscribe({
      next: () => {
        this.groups = this.groups.filter(g => g.id !== group.id);
        if (this.expandedGroupId === group.id) this.expandedGroupId = null;
        if (this.applyGroupId === group.id) this.applyGroupId = null;
      },
      error: () => {}
    });
  }

  expandGroup(group: GrantGroup): void {
    if (this.expandedGroupId === group.id) { this.expandedGroupId = null; return; }
    this.expandedGroupId = group.id;
    this.expandedGroupEntries = [];
    this.newGroupEntryType = 'user';
    this.newGroupEntryId   = '';
    this.newGroupEntryName = '';
    this.newGroupEntryError = '';
    this.groupUserSearchTerm    = '';
    this.groupUserSearchResults = [];
    this.groupUserSearchError   = '';
    this.groupFleetSearchTerm    = '';
    this.groupFleetSearchResults = [];
    this.groupFleetSearchError   = '';
    this.groupEntriesLoading = true;
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.listGroupEntries(userId, group.id).subscribe({
      next: r => { this.expandedGroupEntries = r.entries; this.groupEntriesLoading = false; },
      error: () => { this.groupEntriesLoading = false; }
    });
  }

  searchGroupUsers(): void {
    this.groupUserSearchError = '';
    this.groupUserSearchResults = [];
    const query = this.groupUserSearchTerm.trim();
    if (!query) { this.groupUserSearchError = 'Enter name or user ID to search'; return; }
    this.groupUserSearchLoading = true;
    const adminId = this.auth.currentSession?.userId ?? '';
    const token   = this.auth.currentSession?.accessToken ?? '';
    const server  = this.auth.currentSession?.productionServer ?? 'api2.pixelstarships.com';
    this.ffSvc.searchUsers(adminId, query).subscribe({
      next: r => {
        this.groupUserSearchResults = (r.accounts || []).map((a: any) => ({ userId: a.userId, userName: a.userName, fleetName: a.fleetName }));
        if (!this.groupUserSearchResults.length) {
          this.ffSvc.searchPssUsers(adminId, query, token, server).subscribe({
            next: rr => {
              this.groupUserSearchResults = (rr.accounts || []).map((a: any) => ({ userId: a.userId, userName: a.userName, fleetName: a.fleetName || '' }));
              if (!this.groupUserSearchResults.length) this.groupUserSearchError = 'No users found';
              this.groupUserSearchLoading = false;
            },
            error: () => { this.groupUserSearchError = 'Search failed'; this.groupUserSearchLoading = false; }
          });
          return;
        }
        this.groupUserSearchLoading = false;
      },
      error: () => {
        this.ffSvc.searchPssUsers(adminId, query, token, server).subscribe({
          next: rr => {
            this.groupUserSearchResults = (rr.accounts || []).map((a: any) => ({ userId: a.userId, userName: a.userName, fleetName: a.fleetName || '' }));
            if (!this.groupUserSearchResults.length) this.groupUserSearchError = 'No users found';
            this.groupUserSearchLoading = false;
          },
          error: () => { this.groupUserSearchError = 'Search failed'; this.groupUserSearchLoading = false; }
        });
      }
    });
  }

  chooseGroupUserFromSearch(user: { userId: string; userName: string; fleetName: string }): void {
    this.newGroupEntryId   = user.userId;
    this.newGroupEntryName = user.userName || '';
    this.groupUserSearchTerm    = '';
    this.groupUserSearchResults = [];
    this.groupUserSearchError   = '';
  }

  searchGroupFleets(): void {
    this.groupFleetSearchError = '';
    this.groupFleetSearchResults = [];
    const query = this.groupFleetSearchTerm.trim();
    if (!query) { this.groupFleetSearchError = 'Enter fleet name or ID to search'; return; }
    this.groupFleetSearchLoading = true;
    const adminId = this.auth.currentSession?.userId ?? '';
    const token   = this.auth.currentSession?.accessToken ?? '';
    const server  = this.auth.currentSession?.productionServer ?? 'api2.pixelstarships.com';
    this.ffSvc.searchPssFleets(adminId, query, token, server).subscribe({
      next: rr => {
        const results = (rr.accounts || []).map((a: any) => ({ userId: a.allianceId || '', userName: a.allianceName || '', fleetName: a.allianceName || '' }));
        const seen = new Set<string>();
        this.groupFleetSearchResults = results.filter((item: any) => {
          if (!item.userId || seen.has(item.userId)) return false;
          seen.add(item.userId);
          return true;
        });
        if (!this.groupFleetSearchResults.length) this.groupFleetSearchError = 'No fleets found';
        this.groupFleetSearchLoading = false;
      },
      error: () => { this.groupFleetSearchError = 'Search failed'; this.groupFleetSearchLoading = false; }
    });
  }

  chooseGroupFleetFromSearch(fleet: { userId: string; userName: string; fleetName: string }): void {
    this.newGroupEntryId   = fleet.userId;
    this.newGroupEntryName = fleet.fleetName || fleet.userName || '';
    this.groupFleetSearchTerm    = '';
    this.groupFleetSearchResults = [];
    this.groupFleetSearchError   = '';
  }

  addGroupEntry(): void {
    if (this.expandedGroupId === null || !this.newGroupEntryId) { this.newGroupEntryError = 'Entry ID required'; return; }
    this.newGroupEntryError = '';
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.addGroupEntry(userId, this.expandedGroupId, this.newGroupEntryType, this.newGroupEntryId, this.newGroupEntryName || undefined).subscribe({
      next: r => {
        this.expandedGroupEntries = [...this.expandedGroupEntries.filter(e => e.id !== r.entry.id), r.entry]
          .sort((a, b) => a.entryType.localeCompare(b.entryType) || (a.entryName ?? a.entryId).localeCompare(b.entryName ?? b.entryId));
        const g = this.groups.find(x => x.id === this.expandedGroupId);
        if (g) g.entryCount = this.expandedGroupEntries.length;
        this.newGroupEntryId   = '';
        this.newGroupEntryName = '';
      },
      error: e => { this.newGroupEntryError = e?.error?.error ?? 'Failed'; }
    });
  }

  removeGroupEntry(entry: GrantGroupEntry): void {
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.removeGroupEntry(userId, entry.groupId, entry.id).subscribe({
      next: () => {
        this.expandedGroupEntries = this.expandedGroupEntries.filter(e => e.id !== entry.id);
        const g = this.groups.find(x => x.id === entry.groupId);
        if (g) g.entryCount = this.expandedGroupEntries.length;
      },
      error: () => {}
    });
  }

  openApplyPanel(group: GrantGroup): void {
    this.applyGroupId      = group.id;
    this.applyGroupFlagKeys = new Set();
    this.applyGroupError   = '';
    this.applyGroupSuccess = '';
  }

  closeApplyPanel(): void {
    this.applyGroupId = null;
  }

  toggleApplyFlag(flagKey: string): void {
    if (this.applyGroupFlagKeys.has(flagKey)) {
      this.applyGroupFlagKeys.delete(flagKey);
    } else {
      this.applyGroupFlagKeys.add(flagKey);
    }
    // Force change detection on the Set reference
    this.applyGroupFlagKeys = new Set(this.applyGroupFlagKeys);
  }

  applyGroupToFlags(): void {
    if (this.applyGroupId === null || this.applyGroupFlagKeys.size === 0) {
      this.applyGroupError = 'Select at least one flag';
      return;
    }
    this.applyGroupLoading = true;
    this.applyGroupError   = '';
    this.applyGroupSuccess = '';
    const userId = this.auth.currentSession?.userId ?? '';
    this.ffSvc.applyGroupsToFlags(userId, [this.applyGroupId], Array.from(this.applyGroupFlagKeys)).subscribe({
      next: r => {
        this.applyGroupSuccess = `Done — ${r.added} grant(s) added`;
        this.applyGroupLoading = false;
        this.loadFeatureFlags(); // refresh grant counts
      },
      error: e => { this.applyGroupError = e?.error?.error ?? 'Apply failed'; this.applyGroupLoading = false; }
    });
  }
}
